import type {
  ProjectSuggestion,
  RuleMatchField,
  UnassignedActivity,
} from "../api/types";
import { ruleFieldOf } from "./imputationRules";

/** Branches de tronc : elles ne désignent pas un chantier. */
export const TRUNK_BRANCHES = new Set([
  "main",
  "master",
  "develop",
  "dev",
  "staging",
]);

export type FriseGroupKind =
  "domain" | "code" | "root" | "ssh" | "title" | "tool" | "none";

/** Temps sans projet d'une journée, regroupé par son indice principal : ce
 * qu'une règle d'imputation sait comparer (site, dépôt, dossier, serveur),
 * sinon le titre de session, sinon rien. Deux suggestions différentes pour un
 * même indice font deux groupes : une confirmation n'impute que les créneaux
 * qui portent la sienne. */
export interface FriseGroup {
  key: string;
  kind: FriseGroupKind;
  value: string;
  label: string;
  branch?: string;
  minutes: number;
  eventIds: string[];
  /** Champ de règle qui retient cet indice ; absent, rien à retenir. */
  field?: RuleMatchField;
  suggestion?: ProjectSuggestion;
}

/** « /home/me/dev/client-x/ » donne « client-x ». */
export function lastSegment(path: string): string {
  return path.replace(/\/+$/, "").split("/").pop() || path;
}

/** « /Users/me/repos/kit » donne « ~/repos/kit ». */
function homePath(path: string): string {
  return path.replace(/^\/(?:home|Users)\/[^/]+/, "~");
}

/** « mcp__claude_ai_Google_Drive__search » donne « Google Drive ». */
export function toolLabel(tool: string): string {
  const m = /^mcp__(.+?)__/.exec(tool);
  if (!m) return tool;
  return m[1]!.replace(/^claude_ai_/, "").replace(/_/g, " ");
}

function principal(a: UnassignedActivity): {
  kind: FriseGroupKind;
  value: string;
  branch?: string;
} {
  if (a.context_domain) return { kind: "domain", value: a.context_domain };
  const code = a.context_repo || a.context_project;
  if (code) {
    return {
      kind: "code",
      value: code,
      ...(a.context_branch ? { branch: a.context_branch } : {}),
    };
  }
  const h = a.imputation_hints;
  const root = h?.roots?.[0];
  if (root) return { kind: "root", value: root };
  const host = h?.ssh_hosts?.[0];
  if (host) return { kind: "ssh", value: host };
  if (h?.session_title) return { kind: "title", value: h.session_title };
  const tool = h?.tools?.[0];
  if (tool) return { kind: "tool", value: toolLabel(tool) };
  return { kind: "none", value: "" };
}

function labelOf(kind: FriseGroupKind, value: string): string {
  switch (kind) {
    case "domain":
      return `Site ${value}`;
    case "root":
      return homePath(value);
    case "ssh":
      return `ssh ${value}`;
    case "title":
      return `« ${value} »`;
    case "tool":
      return `outil ${value}`;
    case "none":
      return "sans indice";
    default:
      return value;
  }
}

function fieldOf(kind: FriseGroupKind, value: string) {
  switch (kind) {
    case "domain":
      return "domain";
    case "code":
      return ruleFieldOf(value);
    case "root":
      return "root";
    case "ssh":
      return "ssh_host";
    default:
      return undefined;
  }
}

export function friseGroups(activities: UnassignedActivity[]): FriseGroup[] {
  const byKey = new Map<string, FriseGroup>();
  for (const a of activities) {
    const p = principal(a);
    const sp = a.suggested_project;
    const spKey = sp ? `${sp.project_id}:${sp.work_item_id ?? ""}` : "";
    const key = `${p.kind}:${p.value}:${p.branch ?? ""}|${spKey}`;
    let g = byKey.get(key);
    if (!g) {
      const field = fieldOf(p.kind, p.value);
      g = {
        key,
        kind: p.kind,
        value: p.value,
        label: labelOf(p.kind, p.value),
        ...(p.branch ? { branch: p.branch } : {}),
        minutes: 0,
        eventIds: [],
        ...(field ? { field } : {}),
        ...(sp ? { suggestion: sp } : {}),
      };
      byKey.set(key, g);
    }
    g.minutes += a.worked_minutes;
    if (!g.eventIds.includes(a.event_id)) g.eventIds.push(a.event_id);
  }
  return [...byKey.values()].sort(
    (x, y) =>
      Number(x.kind === "none") - Number(y.kind === "none") ||
      y.minutes - x.minutes,
  );
}

export interface FriseSlot {
  startMin: number;
  endMin: number;
}

/** Ce qui permet de décider à qui va ce temps, tout à plat : indices du kit
 * dédoublonnés, heures des créneaux et projets juste avant et après. */
export interface FriseClues {
  titles: string[];
  roots: string[];
  sshHosts: string[];
  tools: string[];
  slots: FriseSlot[];
  before: string[];
  after: string[];
}

interface ClueBlock {
  startMin: number;
  endMin: number;
  code: string;
  assigned: boolean;
  eventId?: string;
}

function pushUnique(list: string[], value: string | undefined) {
  if (value && !list.includes(value)) list.push(value);
}

export function friseClues(
  activities: UnassignedActivity[],
  eventIds: string[],
  blocks: ClueBlock[],
): FriseClues {
  const ids = new Set(eventIds);
  const clues: FriseClues = {
    titles: [],
    roots: [],
    sshHosts: [],
    tools: [],
    slots: [],
    before: [],
    after: [],
  };
  for (const a of activities) {
    if (!ids.has(a.event_id)) continue;
    const h = a.imputation_hints;
    pushUnique(clues.titles, h?.session_title);
    for (const r of h?.roots ?? []) pushUnique(clues.roots, homePath(r));
    for (const s of h?.ssh_hosts ?? []) pushUnique(clues.sshHosts, s);
    for (const t of h?.tools ?? []) pushUnique(clues.tools, toolLabel(t));
  }
  const assigned = blocks.filter((b) => b.assigned);
  for (const b of blocks) {
    if (b.assigned || !b.eventId || !ids.has(b.eventId)) continue;
    clues.slots.push({ startMin: b.startMin, endMin: b.endMin });
    let prev: ClueBlock | undefined;
    let next: ClueBlock | undefined;
    for (const x of assigned) {
      if (x.endMin <= b.startMin && (!prev || x.endMin > prev.endMin)) prev = x;
      if (x.startMin >= b.endMin && (!next || x.startMin < next.startMin))
        next = x;
    }
    pushUnique(clues.before, prev?.code || undefined);
    pushUnique(clues.after, next?.code || undefined);
  }
  clues.slots.sort((x, y) => x.startMin - y.startMin);
  return clues;
}

/** Les `max` premiers, puis le nombre de ceux qui ne tiennent pas. */
export function capped(list: string[], max = 6): string {
  const shown = list.slice(0, max).join(", ");
  return list.length > max ? `${shown} +${list.length - max}` : shown;
}
